import { useState } from 'react';
import type { ComponentCheck } from './hooks/useTierEvaluation';
import { MachineCheckRow } from './components/machineCheckRow';
import { ReachTierDetailCard } from './components/reachTierDetailCard';
import { RewardsMachineTierHero, RewardsSubpageChrome } from './components/rewardsSubpageChrome';
import { getTierIdentityColor, TIER_2_REACH_BORDER_GRADIENT } from './utils/tierGradients';

interface HowToReachTierProps {
  currentTierName: string;
  currentTierId?: string;
  currentEarnSummary: string;
  targetTierName: string;
  targetTierId?: string;
  targetEarnSummary: string;
  componentChecks: ComponentCheck[];
  onBack: () => void;
}

const AMBIENT_GRADIENT_SUB =
  'linear-gradient(150deg, transparent 0%, rgba(139,0,255,0) 18%, rgba(139,0,255,0.01) 32%, rgba(139,0,255,0.02) 43%, rgba(139,0,255,0.04) 54%, rgba(139,0,255,0.06) 64%, rgba(139,0,255,0.09) 74%, rgba(139,0,255,0.12) 83%, rgba(139,0,255,0.16) 92%, rgba(139,0,255,0.2) 100%)';

export const HowToReachTier = ({
  currentTierName,
  currentTierId,
  currentEarnSummary,
  targetTierName,
  targetEarnSummary,
  componentChecks,
  onBack,
}: HowToReachTierProps) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const failCount = componentChecks.filter((c) => !c.passes).length;
  const isMaxTier = !targetEarnSummary;
  const currentTitleColor = getTierIdentityColor(currentTierId ?? 'tier-1');

  const toggleExpand = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const checkRows = componentChecks.map((check) => (
    <MachineCheckRow
      key={check.component}
      check={check}
      isExpanded={expandedKey === check.component}
      onToggle={() => toggleExpand(check.component)}
    />
  ));

  return (
    <RewardsSubpageChrome onBack={onBack}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', alignItems: 'center' }}>
        <RewardsMachineTierHero
          eyebrow="Machine Tier"
          title={currentTierName.toUpperCase()}
          titleColor={currentTitleColor}
          subtitle={currentEarnSummary || undefined}
        />

        {isMaxTier ? (
          <div
            style={{
              width: '100%',
              marginTop: 4,
              padding: 2,
              borderRadius: 10,
              background: TIER_2_REACH_BORDER_GRADIENT,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                borderRadius: 8,
                background: '#0b0618',
                position: 'relative',
                overflow: 'hidden',
                padding: 12,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: AMBIENT_GRADIENT_SUB,
                  pointerEvents: 'none',
                }}
              />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p
                  style={{
                    margin: '0 0 8px',
                    color: '#8d8aa8',
                    fontSize: 10,
                    lineHeight: '16px',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  MACHINE CHECKS
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                  {checkRows}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <ReachTierDetailCard>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  width: '100%',
                  marginBottom: 6,
                }}
              >
                <p style={{ margin: 0, color: '#ffffff', fontSize: 18, lineHeight: 1.15, fontWeight: 700 }}>
                  Reach {targetTierName}
                </p>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 9999,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    fontWeight: 500,
                    fontSize: 10,
                    lineHeight: '12px',
                    background: '#190D42',
                    color: '#8d8aa8',
                    padding: '3px 8px',
                  }}
                >
                  Locked
                </span>
              </div>

              <p style={{ margin: '0 0 12px', color: '#8d8aa8', fontSize: 12, lineHeight: '15px', fontWeight: 500 }}>
                {targetEarnSummary}
              </p>

              <p
                style={{
                  margin: '0 0 8px',
                  color: '#8d8aa8',
                  fontSize: 10,
                  lineHeight: '16px',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                }}
              >
                MACHINE CHECKS
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                {checkRows}
              </div>
            </ReachTierDetailCard>

            {failCount > 0 && (
              <div
                role="note"
                style={{
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    height: 20,
                    color: 'rgba(112,176,216,0.88)',
                    width: 12,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 300 300" style={{ display: 'block', flexShrink: 0 }}>
                    <g transform="matrix(1 0 0 -1 0 300)">
                      <path
                        fill="currentColor"
                        d="M150 293Q189 293 222.0 274.0Q255 255 274.0 222.0Q293 189 293.0 150.0Q293 111 274.0 78.0Q255 45 222.0 26.0Q189 7 150.0 7.0Q111 7 78.0 26.0Q45 45 26.0 78.0Q7 111 7.0 150.0Q7 189 26.0 222.0Q45 255 78.0 274.0Q111 293 150 293ZM150 270Q118 270 90.5 253.5Q63 237 46.5 209.5Q30 182 30.0 150.0Q30 118 46.5 90.5Q63 63 90.5 46.5Q118 30 150.0 30.0Q182 30 209.5 46.5Q237 63 253.5 90.5Q270 118 270.0 150.0Q270 182 253.5 209.5Q237 237 209.5 253.5Q182 270 150 270ZM150 152Q155 152 158.5 148.5Q162 145 162 141V94Q162 89 158.5 85.5Q155 82 150.0 82.0Q145 82 141.5 85.5Q138 89 138 94V141Q138 145 141.5 148.5Q145 152 150 152ZM150 216Q158 216 163.5 210.5Q169 205 169.0 197.0Q169 189 163.5 183.5Q158 178 150.0 178.0Q142 178 136.5 183.5Q131 189 131.0 197.0Q131 205 136.5 210.5Q142 216 150 216Z"
                      />
                    </g>
                  </svg>
                </span>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <p style={{ margin: 0, color: '#8d8aa8', fontSize: 12, lineHeight: '16px', fontWeight: 500 }}>
                    {failCount} check{failCount === 1 ? '' : 's'} still blocking {targetTierName} — fix{' '}
                    {failCount === 1 ? 'it' : 'them'} to unlock the higher earn rate.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </RewardsSubpageChrome>
  );
};
